import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Helmet } from 'react-helmet-async';
import { 
  ArrowRight, 
  ChevronRight, 
  CheckCircle2, 
  Package, 
  Search, 
  Megaphone, 
  TrendingUp, 
  Truck, 
  Gift, 
  Shield, 
  FileCheck,
  Users,
  BarChart3,
  Target,
  Settings,
  FileText,
  Award,
  Zap
} from 'lucide-react';

const FlipkartServices = () => {
  const caseResults = [
    { metric: 'Sales Growth', value: '180%', description: 'Average increase' },
    { metric: 'Ad ROI', value: '4.2x', description: 'Return on ad spend' },
    { metric: 'Listing Visibility', value: '+85%', description: 'Search ranking' },
    { metric: 'Conversion Rate', value: '+48%', description: 'Improved listings' }
  ];

  return (
    <div className="min-h-screen pt-32">
      <Helmet>
        <title>Flipkart Account Management Services India | Obiquiste Solutions</title>
        <meta name="description" content="Complete Flipkart seller account management: product listing, advertising, account health & brand registration. Grow your Flipkart business with experts." />
        <link rel="canonical" href="https://www.obiquistesolutions.com/flipkart-account-management-services" />
      </Helmet>
      {/* Breadcrumb */}
      <div className="bg-slate-100 py-4 border-b border-slate-200">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-slate-700 hover:text-primary font-medium">Home</Link>
            <ChevronRight size={16} className="text-slate-400" />
            <Link to="/services" className="text-slate-700 hover:text-primary font-medium">Services</Link>
            <ChevronRight size={16} className="text-slate-400" />
            <span className="text-primary font-semibold">Flipkart Account Management</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section 
        className="py-16 md:py-24 lg:py-32 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.03) 0%, rgba(6, 182, 212, 0.03) 100%), linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)'
        }}
      >
        <div className="absolute top-20 right-10 w-72 h-72 bg-blue-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight mb-6">
                Flipkart Account Management <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Services</span>
              </h1>
              <p className="text-xl md:text-2xl font-semibold text-slate-800 mb-4">
                Complete Flipkart Seller Account Management to Grow Your Business
              </p>
              <p className="text-lg leading-relaxed text-slate-600 mb-8">
                Amazing Sales provides complete Flipkart account management services to help brands and sellers grow their business on Flipkart. Our goal is to improve product visibility, optimize listings, and ensure smooth marketplace operations for sustainable growth and increased sales.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact?service=flipkart">
                  <Button className="bg-primary text-white hover:bg-primary-600 shadow-lg hover:shadow-xl px-8 py-6 rounded-full text-base font-semibold">
                    Get Free Flipkart Consultation
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </Link>
                <Link to="/contact?service=flipkart">
                  <Button variant="outline" className="border-2 border-slate-300 hover:border-primary hover:bg-slate-50 px-8 py-6 rounded-full text-base font-semibold">
                    Talk to Flipkart Expert
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              >
                <div className="absolute -inset-4 bg-gradient-to-r from-blue-400/20 to-cyan-400/20 rounded-3xl blur-2xl"></div>
                <div className="relative bg-white rounded-2xl shadow-2xl p-8">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-blue-50 rounded-xl p-4 text-center">
                      <div className="text-3xl font-bold text-blue-600">100+</div>
                      <div className="text-sm text-slate-600">SKUs/Month</div>
                    </div>
                    <div className="bg-cyan-50 rounded-xl p-4 text-center">
                      <div className="text-3xl font-bold text-cyan-600">180%</div>
                      <div className="text-sm text-slate-600">Sales Growth</div>
                    </div>
                    <div className="bg-green-50 rounded-xl p-4 text-center">
                      <div className="text-3xl font-bold text-green-600">4.2x</div>
                      <div className="text-sm text-slate-600">Ad ROI</div>
                    </div>
                    <div className="bg-orange-50 rounded-xl p-4 text-center">
                      <div className="text-3xl font-bold text-orange-600">24/7</div>
                      <div className="text-sm text-slate-600">Support</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ==================== FLIPKART ACCOUNT MANAGEMENT SERVICES ==================== */}
      <section id="account-management" className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Flipkart Account Management Services
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Complete end-to-end account management to help your brand succeed on Flipkart marketplace
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Product Listing Management */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl p-8 border border-slate-200 hover:shadow-xl transition-shadow"
            >
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                <Package className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="font-heading text-xl font-bold mb-4 text-slate-900">
                Product Listing Management
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                Sellers provide raw product data such as images and product details in Excel format, and our team handles the complete listing process on Flipkart Seller Central with accuracy and attention to detail.
              </p>
              <div className="bg-white rounded-lg p-4 border border-blue-100">
                <p className="text-sm font-semibold text-slate-800 mb-2">Listing Capacity:</p>
                <ul className="space-y-2">
                  <li className="flex items-center space-x-2 text-sm text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-blue-500" />
                    <span>Up to <strong>100 SKU</strong> listings per month</span>
                  </li>
                  <li className="flex items-center space-x-2 text-sm text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-blue-500" />
                    <span>Up to <strong>300 SKU</strong> listings in 3-month plan</span>
                  </li>
                </ul>
              </div>
            </motion.div>

            {/* Price & Inventory Updates */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-gradient-to-br from-slate-50 to-green-50 rounded-2xl p-8 border border-slate-200 hover:shadow-xl transition-shadow"
            >
              <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-6">
                <TrendingUp className="w-7 h-7 text-green-600" />
              </div>
              <h3 className="font-heading text-xl font-bold mb-4 text-slate-900">
                Price & Inventory Updates
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                Our team regularly updates product prices and inventory quantities based on discussions with the seller to ensure listings remain active and competitive in the marketplace.
              </p>
              <ul className="space-y-2">
                {['Real-time price adjustments', 'Inventory level monitoring', 'Stock-out prevention', 'Competitive pricing strategy'].map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-2 text-sm text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Flipkart SEO Optimization */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-slate-50 to-purple-50 rounded-2xl p-8 border border-slate-200 hover:shadow-xl transition-shadow"
            >
              <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-6">
                <Search className="w-7 h-7 text-purple-600" />
              </div>
              <h3 className="font-heading text-xl font-bold mb-4 text-slate-900">
                Flipkart SEO Optimization
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                We optimize product titles, descriptions, keywords, and attributes to improve product ranking and search visibility on Flipkart, helping your products reach more potential customers.
              </p>
              <ul className="space-y-2">
                {['Keyword-rich product titles', 'SEO-optimized descriptions', 'Strategic attribute selection', 'Search ranking improvement'].map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-2 text-sm text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Flipkart Advertising Management */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-gradient-to-br from-slate-50 to-orange-50 rounded-2xl p-8 border border-slate-200 hover:shadow-xl transition-shadow"
            >
              <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-6">
                <Megaphone className="w-7 h-7 text-orange-600" />
              </div>
              <h3 className="font-heading text-xl font-bold mb-4 text-slate-900">
                Flipkart Advertising Management
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                We manage Flipkart Sponsored Ads including campaign creation, product selection, bid optimization, and campaign monitoring to increase product visibility and drive sales.
              </p>
              <ul className="space-y-2">
                {['Campaign creation & setup', 'Product selection strategy', 'Bid optimization', 'Performance monitoring'].map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-2 text-sm text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Shipment Creation & Logistics Support */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="bg-gradient-to-br from-slate-50 to-cyan-50 rounded-2xl p-8 border border-slate-200 hover:shadow-xl transition-shadow"
            >
              <div className="w-14 h-14 bg-cyan-100 rounded-xl flex items-center justify-center mb-6">
                <Truck className="w-7 h-7 text-cyan-600" />
              </div>
              <h3 className="font-heading text-xl font-bold mb-4 text-slate-900">
                Shipment Creation & Logistics Support
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                Our team assists with shipment creation, appointment scheduling, and shipment tracking to ensure smooth logistics operations and timely deliveries.
              </p>
              <ul className="space-y-2">
                {['Shipment creation', 'Appointment scheduling', 'Shipment tracking', 'Logistics coordination'].map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-2 text-sm text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-cyan-500 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Flipkart Promotions Support */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="bg-gradient-to-br from-slate-50 to-pink-50 rounded-2xl p-8 border border-slate-200 hover:shadow-xl transition-shadow"
            >
              <div className="w-14 h-14 bg-pink-100 rounded-xl flex items-center justify-center mb-6">
                <Gift className="w-7 h-7 text-pink-600" />
              </div>
              <h3 className="font-heading text-xl font-bold mb-4 text-slate-900">
                Flipkart Promotions Support
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                We assist sellers in participating in Flipkart promotional events and campaigns to increase product visibility and attract more customers during sale events.
              </p>
              <ul className="space-y-2">
                {['Sale event participation', 'Promotional campaign setup', 'Discount strategy', 'Event calendar planning'].map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-2 text-sm text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-pink-500 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Additional Account Management Services */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
            {/* Brand Protection & Brand Gating */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 border border-slate-200 shadow-lg"
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold mb-3 text-slate-900">
                    Brand Protection & Brand Gating
                  </h3>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    We help protect the seller's brand by monitoring unauthorized sellers and assisting with brand gating to prevent others from listing the same products without permission.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {['Unauthorized seller monitoring', 'Brand gating assistance', 'Listing protection', 'Counterfeit prevention'].map((item, idx) => (
                      <div key={idx} className="flex items-center space-x-2 text-sm text-slate-600">
                        <CheckCircle2 className="w-4 h-4 text-red-500" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Claims & Reimbursements */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl p-8 border border-slate-200 shadow-lg"
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <FileCheck className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold mb-3 text-slate-900">
                    Claims & Reimbursements
                  </h3>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    We assist sellers in filing claims for damaged, lost, or incorrect products and help them obtain reimbursements efficiently from Flipkart.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {['Damage claim filing', 'Lost inventory claims', 'Reimbursement tracking', 'Dispute resolution'].map((item, idx) => (
                      <div key={idx} className="flex items-center space-x-2 text-sm text-slate-600">
                        <CheckCircle2 className="w-4 h-4 text-teal-500" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ==================== FLIPKART PRODUCT LISTING & SEO OPTIMIZATION ==================== */}
      <section id="listing-seo" className="py-16 md:py-24 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Flipkart Product Listing & SEO Optimization
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Amazing Sales provides professional Flipkart product listing and SEO optimization services to help sellers improve product visibility and increase sales on Flipkart marketplace.
            </p>
          </motion.div>

          {/* Brand Approval Support - Important Callout */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl p-8 border-l-4 border-blue-500 shadow-lg mb-12"
          >
            <div className="flex items-start space-x-4">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Award className="w-7 h-7 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-2xl font-bold mb-4 text-slate-900">
                  Brand Approval Support
                </h3>
                <p className="text-slate-600 leading-relaxed mb-4">
                  If sellers want to list branded products on Flipkart, brand approval may be required. Our team helps sellers obtain brand approval through Flipkart support so that products can be listed successfully.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50 rounded-xl p-6">
                  <div className="border-l-4 border-orange-400 pl-4">
                    <h4 className="font-semibold text-slate-900 mb-2">For Traders / Resellers:</h4>
                    <p className="text-slate-600 text-sm">A <strong>brand invoice</strong> will be required to verify authorization to sell the brand's products.</p>
                  </div>
                  <div className="border-l-4 border-green-400 pl-4">
                    <h4 className="font-semibold text-slate-900 mb-2">For Brand Owners:</h4>
                    <p className="text-slate-600 text-sm">A <strong>trademark certificate</strong> will be required to verify brand ownership.</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* New Product Listings */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-lg"
            >
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">
                    New Product Listings
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    We create new product listings with SEO-friendly product titles, detailed descriptions, optimized keywords, and proper product attributes to improve search visibility on Flipkart.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {['SEO-friendly titles', 'Detailed descriptions', 'Optimized keywords', 'Proper attributes', 'Category placement', 'Image optimization'].map((item, idx) => (
                  <div key={idx} className="flex items-center space-x-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Existing Listing Optimization */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-lg"
            >
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Settings className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">
                    Existing Listing Optimization
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    If listings already exist, we optimize them by updating product content, improving keywords, updating attributes, and improving SEO-friendly product descriptions for better visibility.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {['Content updates', 'Keyword improvement', 'Attribute updates', 'SEO enhancement', 'Title optimization', 'Description refresh'].map((item, idx) => (
                  <div key={idx} className="flex items-center space-x-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-purple-500" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ==================== FLIPKART ADVERTISING MANAGEMENT SERVICES ==================== */}
      <section id="advertising" className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Flipkart Advertising Management Services
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Flipkart advertising helps increase product visibility and sales. Amazing Sales provides professional advertising management services for Flipkart sellers to maximize their return on ad spend.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Campaign Creation */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-2xl p-8 border border-orange-100"
            >
              <div className="w-14 h-14 bg-orange-500 rounded-xl flex items-center justify-center mb-6">
                <Zap className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-heading text-xl font-bold mb-4 text-slate-900">
                Campaign Creation
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                Our team creates advertising campaigns by selecting the right products for promotion and setting up campaigns properly to achieve optimal results from the start.
              </p>
              <ul className="space-y-2">
                {['Product selection', 'Campaign setup', 'Targeting configuration', 'Budget allocation'].map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-2 text-sm text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Campaign Optimization */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100"
            >
              <div className="w-14 h-14 bg-blue-500 rounded-xl flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-heading text-xl font-bold mb-4 text-slate-900">
                Campaign Optimization
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                We optimize advertising campaigns by adjusting bids, improving targeting, and managing budgets to achieve better results and maximize return on investment.
              </p>
              <ul className="space-y-2">
                {['Bid adjustments', 'Targeting improvement', 'Budget management', 'ROI maximization'].map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-2 text-sm text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Performance Monitoring */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-green-50 to-teal-50 rounded-2xl p-8 border border-green-100"
            >
              <div className="w-14 h-14 bg-green-500 rounded-xl flex items-center justify-center mb-6">
                <BarChart3 className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-heading text-xl font-bold mb-4 text-slate-900">
                Performance Monitoring
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                Our team regularly reviews advertising campaigns and checks campaign performance on an alternate basis to ensure ads are running efficiently and delivering better results.
              </p>
              <ul className="space-y-2">
                {['Regular reviews', 'Performance analysis', 'Efficiency tracking', 'Result optimization'].map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-2 text-sm text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Advertising Goals */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-8 text-white text-center"
          >
            <h3 className="font-heading text-2xl font-bold mb-4">
              Our Advertising Management Goals
            </h3>
            <p className="text-slate-300 max-w-2xl mx-auto">
              The goal of our advertising management is to improve product visibility, increase conversions, and generate higher sales on Flipkart while maintaining optimal advertising costs.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              {[
                { title: 'Improve Visibility', desc: 'Get your products seen by more customers' },
                { title: 'Increase Conversions', desc: 'Turn views into actual sales' },
                { title: 'Generate Higher Sales', desc: 'Grow your revenue on Flipkart' }
              ].map((item, idx) => (
                <div key={idx} className="bg-white/10 rounded-xl p-4">
                  <h4 className="font-semibold mb-2">{item.title}</h4>
                  <p className="text-sm text-slate-400">{item.desc}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Amazing Sales */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Why Choose Amazing Sales?
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Users, title: 'Marketplace Expertise', desc: 'Deep understanding of Flipkart marketplace dynamics and seller requirements', color: 'blue' },
              { icon: BarChart3, title: 'Data-Driven Optimization', desc: 'We use data and analytics to make informed decisions for better results', color: 'green' },
              { icon: Target, title: 'Customized Strategies', desc: 'Tailored solutions based on your specific business needs and goals', color: 'purple' },
              { icon: CheckCircle2, title: 'Dedicated Support', desc: 'Committed team providing consistent support for your Flipkart success', color: 'orange' }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-md border border-slate-200 text-center hover:shadow-lg transition-shadow"
              >
                <div className={`w-14 h-14 bg-${item.color}-100 rounded-full flex items-center justify-center mx-auto mb-4`}>
                  <item.icon className={`w-7 h-7 text-${item.color}-600`} />
                </div>
                <h3 className="font-heading font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl font-bold mb-4">Proven Results for Flipkart Sellers</h2>
            <p className="text-lg text-slate-300">Real performance metrics from our Flipkart clients</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {caseResults.map((result, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="text-center p-8 bg-white/5 rounded-2xl backdrop-blur-sm border border-white/10"
              >
                <div className="text-5xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent mb-2">
                  {result.value}
                </div>
                <h4 className="font-semibold text-lg mb-2">{result.metric}</h4>
                <p className="text-sm text-slate-400">{result.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl mx-auto text-center bg-gradient-to-br from-blue-50 to-cyan-50 rounded-3xl p-12 border border-blue-200">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              Ready to Scale Your Flipkart Business?
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              Get a free Flipkart account audit and discover growth opportunities for your brand with Amazing Sales.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact?service=flipkart">
                <Button className="bg-primary text-white hover:bg-primary-600 px-10 py-7 rounded-full text-lg font-semibold shadow-lg hover:shadow-xl">
                  Get Free Flipkart Audit
                  <ArrowRight className="ml-2" size={24} />
                </Button>
              </Link>
              <Link to="/case-studies">
                <Button variant="outline" className="border-2 border-slate-300 hover:border-primary px-10 py-7 rounded-full text-lg font-semibold">
                  View Case Studies
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FlipkartServices;
